import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ClipboardList, RefreshCw, Phone, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// File d'attente : simulateurs RCDS soumis par les futurs membres, à traiter/rappeler.
export default function SimulateursATraiter() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/member-invitation/admin/simulateurs-a-traiter`, { headers: getAuthHeader() });
      setRows(Array.isArray(data.membres) ? data.membres : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const marquerTraite = async (membreId) => {
    try {
      await axios.post(`${API}/member-invitation/admin/simulateurs/${membreId}/marquer-traite`, {}, { headers: getAuthHeader() });
      setRows((prev) => prev.filter((m) => m.id !== membreId));
      toast.success('Simulateur marqué comme traité');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="mt-4" data-testid="simulateurs-a-traiter">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-amber-800">
            <ClipboardList className="h-4 w-4" /> Simulateurs RCDS à traiter ({rows.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="simulateurs-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun simulateur en attente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Membre</th>
                  <th className="py-2 pr-3">Territoire</th>
                  <th className="py-2 pr-3">Téléphone</th>
                  <th className="py-2 pr-3 text-center">CA HT</th>
                  <th className="py-2 pr-3 text-center">Estimation TTC</th>
                  <th className="py-2 pr-3 text-center">Activités</th>
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody data-testid="simulateurs-rows">
                {rows.map((m) => {
                  const sim = m.simulateur_rcds || {};
                  const est = sim.estimation || {};
                  return (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-amber-50/40">
                      <td className="py-2 pr-3 font-medium text-slate-800">{m.raison_sociale || m.email || '—'}<div className="text-[11px] text-slate-400">{m.numero_membre}</div></td>
                      <td className="py-2 pr-3">{m.territoire || '—'}</td>
                      <td className="py-2 pr-3">
                        {m.telephone ? <a href={`tel:${m.telephone}`} className="text-violet-700 inline-flex items-center gap-1"><Phone className="h-3 w-3" />{m.telephone}</a> : '—'}
                      </td>
                      <td className="py-2 pr-3 text-center">{sim.ca_ht ? `${sim.ca_ht} €` : '—'}</td>
                      <td className="py-2 pr-3 text-center font-medium">{est.total_ttc ? `${est.total_ttc} €` : '—'}</td>
                      <td className="py-2 pr-3 text-center">{Array.isArray(sim.activites) ? sim.activites.length : 0}</td>
                      <td className="py-2"><Badge className="bg-amber-500 text-white border-0">À traiter</Badge></td>
                      <td className="py-2 text-right">
                        <Button variant="outline" size="sm" className="border-emerald-400 text-emerald-700" onClick={() => marquerTraite(m.id)} data-testid={`simulateur-marquer-traite-${m.id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Marquer traité
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
